import './NoticeBar.css';
import { useEffect, useRef } from 'preact/hooks';
import { announce as liveAnnounce } from '../../a11y/LiveRegion';
import type { Ops } from '../../contracts/snapshot';
import { t } from '../../copy/strings';
import { placeName, ufOf } from '../../format/places';
import { fmtTimeAt } from '../../format/time';
import type { UfCode } from '../../model/types';
import { useFeed, useStatics, useUi } from '../../state/hooks';

type Held = NonNullable<Ops['held']>[number];

/** O lugar parado com a preposição: "do Brasil", "da Bahia", "de Belo Horizonte (MG)", "de Lisboa". */
function heldName(h: Held, muns: ReadonlyMap<string, string>, cities: ReadonlyMap<string, string>): string {
  const { level, code = '', uf } = h.scope;
  if (level === 'BR') return `do ${placeName('BR')}`;
  if (level === 'UF') return ufOf(code as UfCode);
  if (level === 'ABROAD_CITY') return `de ${cities.get(code) ?? code}`;
  const name = muns.get(code) ?? code;
  return `de ${uf ? `${name} (${uf})` : name}`;
}

/** Limite do aviso do feed (§6.10, M9); acima disso, descartado. */
export const NOTICE_MAX_CHARS = 200;

/** Porta da região viva única (`announce` de a11y/LiveRegion.tsx, §15.3, DE-15). */
export type AnnounceFn = (text: string, kind: 'notice' | 'response') => void;

/** Remove caracteres de controle (C0, DEL e C1): o texto entra sempre como texto, nunca como HTML (§6.10 bloco 5). */
export function stripControl(text: string): string {
  let out = '';
  for (const ch of text) {
    const c = ch.codePointAt(0) ?? 0;
    if (c < 0x20 || (c >= 0x7f && c <= 0x9f)) continue;
    out += ch;
  }
  return out;
}

/** Aviso do feed válido (M9): string, não vazia depois de trim, até 200 caracteres; senão null (descartado). */
export function cleanFeedNotice(text: unknown): string | null {
  if (typeof text !== 'string' || text.length > NOTICE_MAX_CHARS) return null;
  const out = stripControl(text).trim();
  return out === '' ? null : out;
}

export interface NoticeBarProps {
  /** Porta de anúncio; padrão: `announce` do LiveRegion (§15.3, DE-15). Os testes passam um espião. */
  announce?: AnnounceFn;
}

/**
 * Aviso operacional (`feed.notice`) e transitório (`ui.notice`, de showNotice; tem precedência e, ao sumir, o do feed
 * volta). `<p>` sem papel e sem `aria-live` (F148): cada texto vai ao LiveRegion — o do feed uma vez por texto
 * distinto (`'notice'`), o transitório sempre que muda (`'response'`). Texto novo reanima a entrada (chave do nó).
 */
export function NoticeBar({ announce = liveAnnounce }: NoticeBarProps) {
  const ui = useUi();
  const feed = useFeed();
  const transient = ui.notice === null ? null : stripControl(ui.notice).trim() || null;
  const fromFeed = feed.notice ? cleanFeedNotice(feed.notice.text) : null;
  const { geography } = useStatics();
  const held = feed.held ?? [];
  let fromHeld: string | null = null;
  const first = held[0];
  if (first) {
    const muns = new Map((geography?.municipalities ?? []).map((m) => [m.ibge, m.name]));
    const cities = new Map((geography?.abroad_cities ?? []).map((c) => [c.tse, c.name]));
    const LUGAR = heldName(first, muns, cities);
    const second = held[1];
    fromHeld =
      held.length === 1
        ? t('notice.held', { LUGAR, HHhMM: fmtTimeAt(Date.parse(first.since)) })
        : held.length === 2 && second
          ? t('notice.held.two', { LUGAR, OUTRO: heldName(second, muns, cities) })
          : t('notice.held.more', { LUGAR, N: held.length - 1 });
  }
  const text = transient ?? fromFeed ?? fromHeld;
  const level = transient === null && feed.notice?.level === 'alert' ? 'alert' : 'info';

  const port = useRef(announce);
  port.current = announce;
  const lastFeed = useRef<string | null>(null);
  useEffect(() => {
    if (transient !== null) port.current(transient, 'response');
  }, [transient]);
  useEffect(() => {
    if (fromFeed === null || fromFeed === lastFeed.current) return;
    lastFeed.current = fromFeed;
    port.current(fromFeed, 'notice');
  }, [fromFeed]);

  if (text === null) return null;
  return (
    <p key={text} class="notice-bar" data-testid="notice-bar" data-level={level}>
      {level === 'alert' && <span class="notice-bar__ring" aria-hidden="true" />}
      <span class="notice-bar__text">{text}</span>
    </p>
  );
}
